<template>
  <SoWidget plain no-border>
    <template v-if="!isLoading">
      <UnavailableMeterpointAlert
        v-for="meterpoint in current.unavailableMeterpoints.data"
        :key="meterpoint.id"
        :meterpoint="meterpoint"
        class="mb-2"
      />
      <SoRow v-if="current.meterpoints.data.length > 0" class="mb-1">
        <SoCol
          v-for="(meterpoint, index) in current.meterpoints.data"
          :key="index"
          :default-width="12"
        >
          <MeterpointsDetails
            v-if="meterpointDetails(meterpoint.id).isFetched"
            ref="meterpoint-details"
            :meterpoint="meterpointDetails(meterpoint.id).data"
            :mop="meterpoint.mop"
            :mam="meterpoint.mam"
            :meterpoint-consents="meterpointConsentById(meterpoint.identifier)"
            :has-agreement-with-tou-product="
              meterpointHasTou(meterpoint.identifier)
            "
            :energy-usage-checks="energyUsageByMeterpoint[meterpoint.id]"
            :daily-usage-data="
              filterUsageForMeterpoint(dailyUsage?.data, meterpoint.id)
            "
            :hh-usage-data="
              filterUsageForMeterpoint(hhUsage?.data, meterpoint.id)
            "
            @open-change-frequency-modal="openChangeFrequencyModal"
          />
        </SoCol>
      </SoRow>
      <SoAlert
        v-else-if="!current.unavailableMeterpoints.data.length"
        class="my-2"
        type="info"
        title="No meterpoint details available"
        data-testid="no-meter-points-available"
      />
    </template>
    <SoSkeleton v-else type="card" />
  </SoWidget>
  <ChangeReadFrequencyModal
    v-if="showChangeFrequencyModal"
    v-model="showChangeFrequencyModal"
    :has-tou="selectedMeterpointHasTou"
    :has-export="hasExportMeterpoint"
    :has-export-only="hasExportOnlyMeterpoint"
    :is-gas="selectedMeterpoint?.meterpointType === 'MPRN'"
    :current-read-frequency="selectedMeterpointReadFrequency"
    :current-settlement-frequency="selectedMeterpointSettlementFrequency"
    data-testid="change-read-frequency-modal"
  />
</template>

<script lang="ts">
  import { defineComponent } from "vue"
  import { useCurrentAccountStore } from "@/stores/currentAccount"
  import SoWidget from "@/components/SoWidget.vue"
  import SoRow from "@/components/SoRow.vue"
  import SoCol from "@/components/SoCol.vue"
  import SoAlert from "@/components/SoAlert.vue"
  import SoSkeleton from "@/components/SoSkeleton.vue"
  import MeterpointsDetails from "../components/MeterpointsDetails.vue"
  import { ConsentsService } from "@/services/ConsentsService"
  import {
    ConsentSetting,
    EnergyUsageFuelType,
  } from "@/api/backofficeRest/generated/model"
  import ChangeReadFrequencyModal from "../modals/ChangeReadFrequencyModal.vue"
  import UnavailableMeterpointAlert from "@/views/customer/components/UnavailableMeterpointAlert.vue"

  import { IMeterpointConsent } from "@/models/meterpointConsent/meterpointConsent.types"
  import { IMeterpoint } from "@/models/meterpoint/meterpoint.types"
  import { SmartReadFrequencies } from "@/models/consent/consent.types"
  import { EnergyUsageService } from "@/services/EnergyUsageService"
  import { IEnergyUsage } from "@/models/energyUsage/energyUsage.types"
  import { FeaturesService } from "@/services/admin/FeaturesService"
  import { AccountService } from "@/services/AccountService"
  import dayjs from "dayjs"
  import { checkEnergyUsageForMeterpoint } from "@/services-handlers/checkMeterEnergyUsage"
  import { HealthCheckInfo } from "@/services-handlers/getAccountHealthChecks"

  // Daily: midnight-aligned so day boundaries match calendar days
  const DAILY_USAGE_LOOKBACK_DAYS = 7
  const toDaily = dayjs().startOf("day").toISOString()
  const fromDaily = dayjs()
    .subtract(DAILY_USAGE_LOOKBACK_DAYS, "days")
    .startOf("day")
    .toISOString()

  // HH: full previous day (00:00 yesterday to 00:00 today) — API limit is 1 day
  const fromHh = dayjs().subtract(1, "day").startOf("day").toISOString()
  const toHh = dayjs().startOf("day").toISOString()

  export default defineComponent({
    components: {
      SoWidget,
      SoRow,
      SoCol,
      SoAlert,
      SoSkeleton,
      MeterpointsDetails,
      ChangeReadFrequencyModal,
      UnavailableMeterpointAlert,
    },
    data() {
      return {
        current: useCurrentAccountStore(),
        ConsentsService: ConsentsService,
        showDetails: false,
        showChangeFrequencyModal: false,
        selectedMeterpoint: null as IMeterpoint | null,
        selectedMeterpointConsents: null as IMeterpointConsent[] | null,
      }
    },
    computed: {
      isLoading() {
        return (
          this.current.meterpoints.isFetching ||
          this.meterpointConsents.isFetching ||
          this.current.agreementsByMeterPoint.isFetching
        )
      },
      meterpointIds() {
        return this.current.meterpoints.data.map((meterpoint) => meterpoint.id)
      },
      meterpointConsents() {
        return this.ConsentsService.getPrioritisedMeterpointConsents(
          this.current.coreId.data,
        )
      },
      selectedMeterpointHasTou() {
        return this.selectedMeterpoint
          ? this.meterpointHasTou(this.selectedMeterpoint.identifier)
          : false
      },
      hasExportMeterpoint() {
        return this.current.meterpoints.data.some((mp) => mp.isExport)
      },
      hasExportOnlyMeterpoint() {
        return this.current.meterpoints.data.every((mp) => mp.isExport)
      },
      isEnergyUsageEnabled(): boolean {
        return FeaturesService.isFeatureActive("ENERGY_USAGE_NOVA") ?? false
      },
      accountId(): number | null {
        return (
          AccountService.getAccount(this.current.accountNumber).data?.id ?? null
        )
      },
      dailyUsage() {
        if (!this.isEnergyUsageEnabled || !this.accountId) return null
        return EnergyUsageService.getDailyUsage(
          this.accountId,
          fromDaily,
          toDaily,
        )
      },
      hhUsage() {
        if (!this.isEnergyUsageEnabled || !this.accountId) return null
        return EnergyUsageService.getHalfHourlyUsage(
          this.accountId,
          fromHh,
          toHh,
        )
      },
      energyUsageByMeterpoint(): Record<number, HealthCheckInfo[]> {
        if (!this.dailyUsage?.data || !this.hhUsage?.data) return {}
        const result: Record<number, HealthCheckInfo[]> = {}
        for (const mp of this.current.meterpoints.data) {
          if (!mp.hasSmartMeter || mp.isExport) continue
          result[mp.id] = checkEnergyUsageForMeterpoint(
            this.dailyUsage.data,
            this.hhUsage.data,
            mp.meterpointType,
          )
        }
        return result
      },
      selectedMeterpointReadFrequency() {
        if (!this.selectedMeterpointConsents || !this.selectedMeterpoint)
          return null

        const isElectricity = this.selectedMeterpoint.meterpointType === "MPAN"

        const billingConsent = this.selectedMeterpointConsents.find(
          (consent) =>
            isElectricity
              ? consent.consentDefinition === "MPAN_BILLING"
              : consent.consentDefinition === "MPRN_BILLING",
        )

        return this.mapConsentSettingToFrequency(billingConsent?.setting)
      },

      selectedMeterpointSettlementFrequency() {
        if (!this.selectedMeterpointConsents) return null

        const settlementConsent = this.selectedMeterpointConsents.find(
          (consent) => consent.consentDefinition === "MPAN_SETTLEMENT",
        )

        return this.mapConsentSettingToFrequency(settlementConsent?.setting)
      },
    },
    methods: {
      meterpointDetails(meterpointId: number) {
        const mp = this.current.meterpoints.data.find(
          (m) => m.id === meterpointId,
        )
        return {
          data: mp,
          isFetched: !!mp,
          isFetching: this.current.meterpoints.isFetching,
        }
      },
      meterpointHasTou(identifier: string): boolean {
        const agreements =
          this.current.agreementsByMeterPoint.data[identifier] ?? []
        return agreements.some(
          (agreement) =>
            (agreement.isActive || agreement.isPending) &&
            agreement.isTouTariff,
        )
      },
      meterpointConsentById(meterpointId: string) {
        return this.ConsentsService.getMeterpointConsentById(
          this.current.coreId.data,
          meterpointId,
        ).data
      },
      openChangeFrequencyModal(meterpoint: IMeterpoint) {
        this.selectedMeterpoint = meterpoint
        this.selectedMeterpointConsents = this.meterpointConsentById(
          meterpoint.identifier,
        )
        this.showChangeFrequencyModal = true
      },
      filterUsageForMeterpoint(
        usage: IEnergyUsage[] | null | undefined,
        meterpointId: number,
      ): IEnergyUsage[] {
        if (!usage) return []
        const mp = this.current.meterpoints.data.find(
          (m) => m.id === meterpointId,
        )
        if (!mp || mp.isExport) return []
        const fuelType =
          mp.meterpointType === "MPAN"
            ? EnergyUsageFuelType.ELECTRICITY
            : EnergyUsageFuelType.GAS
        return usage.filter((u) => u.fuelType === fuelType)
      },
      mapConsentSettingToFrequency(setting?: ConsentSetting) {
        switch (setting) {
          case ConsentSetting.HALF_HOURLY:
            return SmartReadFrequencies.HALF_HOURLY
          case ConsentSetting.DAILY:
            return SmartReadFrequencies.DAILY
          case ConsentSetting.MONTHLY:
            return SmartReadFrequencies.MONTHLY
          default:
            return null
        }
      },
    },
  })
</script>
